import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { environment } from '@environments/environment';
import { ModalController } from '@ionic/angular';
import { regexList } from '@shared/constants/regex-list';
import { SearchOption } from '@shared/models/search-option';
import { SearchOptionGroup } from '@shared/models/search-option-group';
import { SelectOption } from '@shared/models/select-option';
import { MultiSelectComponent } from '../multi-select/multi-select.component';
import moment from 'moment';
import { UserSelectorContainerComponent } from '@features/users/shared/containers/user-selector-container/user-selector-container.component';

@Component({
  selector: 'app-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.scss'],
  standalone: false,
})
export class AdvancedSearchComponent implements OnInit, OnChanges {
  @Input() searchOptionGroups: SearchOptionGroup[];
  @Input() noResultsFound: boolean = false;
  @Input() resetSearch: boolean = false;
  @Output() searchOptionsChange = new EventEmitter<SearchOption[]>();
  @Output() filtersChange = new EventEmitter<any>();
  @Output() resetSearchOptionsEvent = new EventEmitter<Boolean>();
  newSearchOptionGroups: SearchOptionGroup[];
  inactiveSearchOptionGroups: SearchOptionGroup[];
  inactiveSearchOptions: SearchOption[];
  activeSearchOptions: SearchOption[];
  focusedSearchOption: SearchOption | null;
  searchValue: string;
  currencySymbol = environment.currency_symbol;
  regexList = regexList;
  isOpen = false;
  isEditMode = false;
  toDeleteFilter = false;
  filterToDelete: SearchOption;
  maxDate = moment().add(3, 'years').format('YYYY-MM-DD');

  @ViewChild('filterInput') filterInput: any;

  constructor(private modalController: ModalController) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!!this.searchOptionGroups) {
      this.newSearchOptionGroups = [...this.searchOptionGroups];
      this.updateSearchOptionVariables();
    }
  }

  updateSearchOptionVariables() {
    this.activeSearchOptions = this.filterActiveOptions();
    this.isEditMode = this.activeSearchOptions.filter((option) => option.editMode).length > 0;
    this.filterInactiveOptionGroups();
    let editItem = this.activeSearchOptions.find((item) => item.editMode);
    if (!!editItem && !editItem?.suggestedValues && ['string', 'number'].includes(editItem.type)) {
      setTimeout(() => {
        const element = document.getElementById('search-' + editItem?.key);
        if (element) {
          element.focus();
        }
      });
    }
  }

  filterActiveOptions(): SearchOption[] {
    if (this.toDeleteFilter) {
      this.toDeleteFilter = false;
    }

    let selectedOptions = this.newSearchOptionGroups
      .flatMap((optionGroup) => optionGroup.searchOptions.map((option) => option))
      .filter((option) => option.value !== undefined);

    if (selectedOptions.length === 0) {
      return [];
    }
    if (!this.activeSearchOptions) {
      return selectedOptions;
    }
    if (this.activeSearchOptions.length === selectedOptions.length) {
      return this.activeSearchOptions;
    }

    if (this.activeSearchOptions.length <= selectedOptions.length) {
      let keysAlreadySelected = this.activeSearchOptions?.map((item) => item.key);
      let newElements = selectedOptions?.filter((item) => !keysAlreadySelected.includes(item.key));

      return this.activeSearchOptions?.concat(newElements);
    } else {
      let remainingKeys = selectedOptions?.map((item) => item.key);
      let valueToRemove = this.activeSearchOptions?.filter((item) => !remainingKeys.includes(item.key));

      this.filterInput?.nativeElement.focus();
      return this.activeSearchOptions?.filter((item) => item.key !== valueToRemove[0].key);
    }
  }

  filterInactiveOptionGroups() {
    if (!!this.searchValue) {
      const allOptions = this.newSearchOptionGroups.flatMap((optionGroup) =>
        optionGroup.searchOptions.map((option) => option),
      );
      const filteredOptionKeys = this.filterInactiveOptionsByRegex(allOptions).map((option) => option.key);
      this.inactiveSearchOptionGroups = this.newSearchOptionGroups
        .filter(
          (optionGroup) =>
            optionGroup.searchOptions.filter((option) =>
              filteredOptionKeys.some((optionKey) => optionKey == option.key),
            ).length > 0,
        )
        .map((optionGroup) => {
          return {
            ...optionGroup,
            searchOptions: this.filterInactiveOptions(optionGroup.searchOptions),
          };
        });
    } else {
      this.inactiveSearchOptionGroups = this.newSearchOptionGroups
        .filter((optionGroup) => optionGroup.searchOptions.filter((option) => option.value == undefined).length > 0)
        .map((optionGroup) => {
          return {
            ...optionGroup,
            searchOptions: this.filterInactiveOptions(optionGroup.searchOptions),
          };
        });
    }
    this.inactiveSearchOptions = this.inactiveSearchOptionGroups.flatMap((optionGroup) =>
      optionGroup.searchOptions.map((option) => option),
    );
  }

  filterInactiveOptions(searchOptions: SearchOption[]): SearchOption[] {
    if (!!this.searchValue) {
      return this.filterInactiveOptionsByRegex(searchOptions);
    } else {
      return searchOptions.filter((option) => option.value == undefined);
    }
  }

  filterInactiveOptionsByRegex(searchOptions: SearchOption[]): SearchOption[] {
    let matchingOptions = searchOptions.filter((option) =>
      option.value == undefined && option.filter
        ? option.filter?.test(this.searchValue?.trim())
        : option.value == undefined,
    );
    if (matchingOptions.length > 1) {
      let filters = matchingOptions.map((option) => option.filter);
      if (filters.includes(this.regexList.group_id)) {
        return matchingOptions.filter((option) => option.filter == this.regexList.group_id);
      }
    }
    return matchingOptions;
  }

  async editSearchOption(searchOption: SearchOption, editMode: boolean, value: any) {
    let trimmedValue = ['string'].includes(searchOption.type) ? value?.trim() : value;
    if (!trimmedValue) {
      this.removeSearchOption(searchOption);
    } else if (searchOption.type == 'multiSelect') {
      const selectedOptions = searchOption.suggestedValues?.filter((suggestedValue) =>
        trimmedValue.includes(suggestedValue.value),
      );
      this.openMultiSelectModal(searchOption, selectedOptions);
    } else if (searchOption.type == 'userSelect') {
      this.openUserSelectModal(searchOption);
    } else {
      const valueChanged = this.updateSearchOptionGroups(searchOption, editMode, trimmedValue);
      this.isOpen = false;
      this.updateSearchOptionVariables();
      if (valueChanged) this.emitSearchOptionUpdates();
    }
    setTimeout(() => this.filterInput?.nativeElement.focus(), 0);
  }

  removeSearchOption(searchOption: SearchOption) {
    if (!searchOption) return;
    const valueChanged = this.updateSearchOptionGroups(searchOption, false, undefined);
    this.isOpen = false;
    this.updateSearchOptionVariables();
    if (valueChanged) this.emitSearchOptionUpdates();
    setTimeout(() => this.filterInput?.nativeElement.focus(), 0);
  }

  removeAllSearchOptions(emitUpdate = true) {
    this.newSearchOptionGroups.forEach((optionGroup) =>
      optionGroup.searchOptions.forEach((option) => (option.value = undefined)),
    );
    this.updateSearchOptionVariables();
    if (emitUpdate) this.emitSearchOptionUpdates();
  }

  resetSearchOptions() {
    this.isOpen = false;
    this.removeAllSearchOptions();
    this.resetSearchOptionsEvent.emit();
  }

  async selectSearchOption(searchOption: SearchOption) {
    if (!searchOption) return;
    if (searchOption.type == 'multiSelect') {
      this.openMultiSelectModal(searchOption);
    } else if (searchOption.type == 'userSelect') {
      this.openUserSelectModal(searchOption);
    } else {
      this.isOpen = false;
      this.filterInput?.nativeElement?.blur();
      let editMode = !this.searchValue;
      let value = !this.searchValue ? '' : this.searchValue.trim();
      const valueChanged = this.updateSearchOptionGroups(searchOption, editMode, value);
      if (!!this.searchValue) {
        this.filterInput.nativeElement.value = '';
        this.searchValue = '';
      }
      this.updateSearchOptionVariables();
      if (valueChanged) this.emitSearchOptionUpdates();
    }
  }

  async openMultiSelectModal(searchOption: SearchOption, selectedOptions: SelectOption[] = []) {
    const modal = await this.modalController.create({
      component: MultiSelectComponent,
      componentProps: {
        selectOptions: searchOption.suggestedValues,
        title: searchOption.title,
        selectedOptions: selectedOptions,
      },
      id: 'multi-select-modal',
      backdropDismiss: false,
      cssClass: 'layered-modal drawer-style-sm',
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (!!data?.selectedOptions) {
      if (data.selectedOptions.length == 0) {
        this.removeSearchOption(searchOption);
      } else {
        const selectedValues = (data.selectedOptions as SelectOption[]).map((selectedOption) =>
          String(selectedOption.value),
        );
        const values = selectedValues;
        const valueChanged = this.updateSearchOptionGroups(searchOption, false, values);
        this.updateSearchOptionVariables();
        if (valueChanged) this.emitSearchOptionUpdates();
      }
    }
  }

  async openUserSelectModal(searchOption: SearchOption) {
    const modal = await this.modalController.create({
      component: UserSelectorContainerComponent,
      id: 'user-select-modal',
      backdropDismiss: false,
      cssClass: 'layered-modal drawer-style-sm',
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data?.user) {
      const valueChanged = this.updateSearchOptionGroups(
        searchOption,
        false,
        data?.user.id,
        data?.user.attributes.full_name,
      );
      this.updateSearchOptionVariables();
      if (valueChanged) this.emitSearchOptionUpdates();
    }
  }

  updateAllSearchOptionGroups(searchOptionGroups: SearchOptionGroup[]) {
    this.newSearchOptionGroups = [...searchOptionGroups];
    this.updateSearchOptionVariables();
    this.emitSearchOptionUpdates();
  }

  updateSearchOptionGroups(searchOption: SearchOption, editMode: boolean, value?: any, readableValue?: string): any {
    const optionGroupIndex = this.newSearchOptionGroups.findIndex((optionGroup) =>
      optionGroup.searchOptions.some((option) => option.key == searchOption?.key),
    );
    const optionIndex = this.newSearchOptionGroups[optionGroupIndex]?.searchOptions.findIndex(
      (option) => option.key == searchOption?.key,
    );
    const oldValue = this.newSearchOptionGroups[optionGroupIndex]?.searchOptions[optionIndex].value;
    if (searchOption?.type !== 'multiSelect') {
      const suggestedValue = searchOption?.suggestedValues?.find(
        (suggestedValue) => suggestedValue.title.toLowerCase() == value,
      );
      if (!!suggestedValue) {
        value = suggestedValue.title;
      }
    }

    this.newSearchOptionGroups[optionGroupIndex].searchOptions[optionIndex].value = value;
    if (readableValue) {
      this.newSearchOptionGroups[optionGroupIndex].searchOptions[optionIndex].readableValue = readableValue;
    }
    this.newSearchOptionGroups[optionGroupIndex].searchOptions[optionIndex].editMode = editMode;

    let valueChanged = value !== oldValue;
    if ((oldValue == '' && value == undefined) || (oldValue == undefined && value == '')) valueChanged = false;
    return valueChanged;
  }

  updateSearchValue(event: any) {
    if (event.key !== 'Backspace') this.toDeleteFilter = false;
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      this.searchValue = event.target.value;
      this.isOpen = true;
      this.filterInactiveOptionGroups();
      this.inactiveSearchOptions = this.inactiveSearchOptionGroups.flatMap((optionGroup) =>
        optionGroup?.searchOptions.map((option) => option),
      );
      this.focusedSearchOption = this.inactiveSearchOptions[0];
    }
    if (!this.filterInput?.nativeElement.value) {
      this.focusedSearchOption = null;
    }
  }

  openAdvancedSearch(event: any) {
    if (
      ['mainSearchbox', 'filterInput'].includes(event.target?.id) ||
      event.target?.parentElement?.id == 'mainSearchbox'
    ) {
      this.activeSearchOptions.forEach((activeSearchOption) => {
        if (!activeSearchOption.value) {
          this.removeSearchOption(activeSearchOption);
        }
      });
      let editItem = this.activeSearchOptions.find((item) => item.editMode);
      if (!!editItem) {
        editItem.editMode = false;
        this.updateSearchOptionVariables();
      }
      this.isOpen = true;
      this.filterInput?.nativeElement?.focus();
    }
  }

  closeAdvancedSearch(event: any) {
    event.preventDefault();
    if (!event?.relatedTarget?.id?.includes('search-option-')) {
      this.isOpen = false;
    }
  }

  emitSearchOptionUpdates() {
    let searchOptions = this.newSearchOptionGroups
      .flatMap((optionGroup) => optionGroup.searchOptions.map((option) => option))
      .filter((option) => option.value !== undefined);
    this.searchOptionsChange.emit(searchOptions);
    this.filtersChange.emit(
      Object.fromEntries(
        searchOptions.map((option) => [
          option.key,
          option.type !== 'multiSelect' && option.suggestedValues && option.suggestedValues?.length > 0
            ? option.suggestedValues?.find((suggestedValue) => suggestedValue.title == option.value)?.value
            : option.value,
        ]),
      ),
    );
    setTimeout(() => this.filterInput?.nativeElement.focus(), 0);
  }

  removeFilterOnBackspace(event: any, activeOption: SearchOption) {
    if (event.key === 'Backspace') {
      if (this.filterInput?.nativeElement.value.length == 0 && !this.toDeleteFilter) {
        this.toDeleteFilter = true;
        if (event.target.getAttribute('id') === 'filterInput') {
          this.filterToDelete = this.activeSearchOptions[this.activeSearchOptions.length - 1];
        } else {
          this.filterToDelete = activeOption;
        }
      } else if (this.toDeleteFilter) {
        this.removeSearchOption(this.filterToDelete);
        this.toDeleteFilter = false;
      }
    } else if (this.filterInput?.nativeElement.value.length > 0 && this.toDeleteFilter) {
      this.toDeleteFilter = false;
    }
  }

  traverseSearchOptions(event: any) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'Tab') {
      const focusedSearchOptionIndex = this.inactiveSearchOptions.findIndex(
        (option) => option.key == this.focusedSearchOption?.key,
      );

      if (focusedSearchOptionIndex < this.inactiveSearchOptions.length - 1) {
        this.focusedSearchOption = this.inactiveSearchOptions[focusedSearchOptionIndex + 1];
      } else {
        this.focusedSearchOption = this.inactiveSearchOptions[0];
      }
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      const focusedSearchOptionIndex = this.inactiveSearchOptions.findIndex(
        (option) => option.key == this.focusedSearchOption?.key,
      );

      if (focusedSearchOptionIndex > 0) {
        this.focusedSearchOption = this.inactiveSearchOptions[focusedSearchOptionIndex - 1];
      } else {
        this.focusedSearchOption = this.inactiveSearchOptions[this.inactiveSearchOptions.length - 1];
      }
    }
  }
}
